// Price history page (`#/prices`, D72): past prices of one category, searched by item in the
// browser. Rows come from PriceHistory_<year> (the history import, D69, and quotes chosen in
// the portal). Money is minor units.

export const FIRST_PRICE_YEAR = 2021;

export interface PriceRow {
  item: string;
  /** The item key shared with the quote price check (normaliseItem). */
  key: string;
  supplierName: string;
  date: string;
  qty: number;
  unitPrice: number;
  currency: string;
  /** Unit price in SAR, minor units; null when there is no fixed rate (EGP). */
  unitPriceSAR: number | null;
  poNo: string;
  prNo: string;
  department: string;
  vertical: string;
  /** PO (history import) or quote (chosen in the portal). */
  source: string;
}

export interface PriceHistoryBody {
  category: string;
  categories: { id: string; nameEN: string; nameAR: string }[];
  rows: PriceRow[];
  /** True when a year hit the read cap; the newest rows may be missing. */
  capped: boolean;
}

export interface PriceSummary {
  count: number;
  /** In SAR (minor units), over rows that have a SAR price. */
  minSAR: number | null;
  avgSAR: number | null;
  maxSAR: number | null;
  last: PriceRow | null;
}

/** Every word of the search must appear in the item (same normalising as the item key). */
export function matchPrices(
  rows: readonly PriceRow[],
  q: string,
  normalise: (s: string) => string,
): PriceRow[] {
  const words = normalise(q).split(' ').filter(Boolean);
  const hits = words.length ? rows.filter((r) => words.every((w) => r.key.includes(w))) : [...rows];
  return hits.sort((a, b) => b.date.localeCompare(a.date) || b.poNo.localeCompare(a.poNo));
}

export function summarise(rows: readonly PriceRow[]): PriceSummary {
  const sar = rows.map((r) => r.unitPriceSAR).filter((v): v is number => v !== null);
  const sorted = [...rows].sort((a, b) => b.date.localeCompare(a.date));
  return {
    count: rows.length,
    minSAR: sar.length ? Math.min(...sar) : null,
    avgSAR: sar.length ? Math.round(sar.reduce((s, v) => s + v, 0) / sar.length) : null,
    maxSAR: sar.length ? Math.max(...sar) : null,
    last: sorted[0] ?? null,
  };
}

/**
 * Past prices for one request line (D85): rows sharing at least half of the line's words
 * (three letters or more), best match first, then newest. Looser than matchPrices, because a
 * requester's wording rarely matches the old PO text exactly.
 */
export function similarPrices(
  rows: readonly PriceRow[],
  description: string,
  normalise: (s: string) => string,
  limit = 5,
): PriceRow[] {
  const words = [...new Set(normalise(description).split(' '))].filter((w) => w.length >= 3);
  if (!words.length) return [];
  const need = Math.max(1, Math.ceil(words.length / 2));
  return rows
    .map((r) => {
      const keyWords = new Set(r.key.split(' '));
      return { r, score: words.filter((w) => keyWords.has(w)).length };
    })
    .filter((x) => x.score >= need)
    .sort((a, b) => b.score - a.score || b.r.date.localeCompare(a.r.date))
    .slice(0, limit)
    .map((x) => x.r);
}
